import type { ReactNode } from "react";
import { useAuthStore } from "@/stores/auth";

/**
 * Page heading helper that appends the active organization name.
 *
 * Usage:
 *   <OrgPageTitle>Crew</OrgPageTitle>
 *   → "Crew · Skeldon"
 *
 * Renders just the page name when there's no active org (e.g. personal-org-only
 * user scenarios) — the OrgBanner above already takes care of the visual cue.
 */
export function OrgPageTitle({
  children,
  subtitle,
}: {
  children: ReactNode;
  subtitle?: ReactNode;
}) {
  const activeOrgId = useAuthStore((s) => s.activeOrganizationId);
  const orgs = useAuthStore((s) => s.organizations);
  const orgName = orgs.find((o) => o.id === activeOrgId)?.name ?? null;

  return (
    <div>
      <h1 className="text-2xl font-semibold tracking-tight text-ink">
        {children}
        {orgName ? (
          <span className="font-normal text-ink-3"> · {orgName}</span>
        ) : null}
      </h1>
      {subtitle ? (
        <p className="mt-1 text-sm text-ink-3">{subtitle}</p>
      ) : null}
    </div>
  );
}
